import { useQuery } from '@tanstack/react-query';
import { type MouseEvent, type PropsWithChildren, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { featureFlagAdminApi } from '@/api/feature-flag-admin';
import { supportApi } from '@/api/support';
import { UserAvatar } from '@/components/common/UserAvatar';
import {
    Flag,
    DirectInbox,
    Logout,
    HamburgerMenu,
    Add,
    Setting2,
    Lifebuoy,
    Card,
    Message,
    DocumentText,
    Notepad2,
    type IconComponent,
} from '@/components/icons';
import { SidebarBrandLogo } from '@/components/sidebar/SidebarBrandLogo';
import SidebarHeaderContent from '@/components/sidebar/SidebarHeaderContent';
import SidebarUpgradeButton from '@/components/sidebar/SidebarUpgradeButton';
import { ThemeMenuItems } from '@/components/theme/ThemeToggle';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
    SidebarProvider as Provider,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { LOADING_MESSAGE } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { cn } from '@/lib/utils';
import {
    BillingPage,
    FeatureFlagsAdminPage,
    HelpPage,
    PatientsPage,
    ProfilePage,
    ReportPage,
    SupportInboxPage,
} from '@/pages/constants';
import NewAnamnesisWizard from '@/pages/patients/anamnesis/NewAnamnesisWizard';
import { anamnesisEditorRoute } from '@/pages/patients/anamnesis/use-anamnesis-actions';
import { createPageUrl } from '@/utils';
import { FeedbackModal } from '../modals/FeedbackModal';
import { useModalContext } from '../ui/modal-provider';
import { navigationItems } from './constants';
type Props = {
    onLogout: () => void | Promise<void>;
} & PropsWithChildren;

type AppSidebarItemProps = {
    id?: string;
    label: string;
    icon: IconComponent;
    to?: string;
    active?: boolean;
    onClick?: () => void;
};

const SIDEBAR_MENU_ITEM_CLASS =
    'group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center';

const SIDEBAR_MENU_BUTTON_CLASS =
    'h-10 gap-3 rounded-xl px-3 text-sm text-sidebar-foreground transition-[background-color,color,box-shadow] duration-150 ease-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!h-10 group-data-[collapsible=icon]:!w-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center';

const SIDEBAR_MENU_ACTIVE_CLASS = 'bg-sidebar-accent font-medium text-sidebar-accent-foreground';

const SIDEBAR_MENU_ICON_SLOT_CLASS =
    'flex h-5 w-5 shrink-0 items-center justify-center group-data-[collapsible=icon]:m-0';

const SIDEBAR_MENU_ICON_CLASS = 'h-4 w-4';

function AppSidebarItem({
    id,
    label,
    icon: Icon,
    to,
    active = false,
    onClick,
}: AppSidebarItemProps): ReactElement {
    const content = (
        <>
            <span className={SIDEBAR_MENU_ICON_SLOT_CLASS}>
                <Icon className={cn(SIDEBAR_MENU_ICON_CLASS, active && 'text-brand')} />
            </span>
            <span className="group-data-[collapsible=icon]:sr-only">{label}</span>
        </>
    );

    return (
        <SidebarMenuItem className={SIDEBAR_MENU_ITEM_CLASS}>
            <SidebarMenuButton
                id={id}
                asChild={Boolean(to)}
                isActive={active}
                onClick={onClick}
                className={cn(SIDEBAR_MENU_BUTTON_CLASS, active && SIDEBAR_MENU_ACTIVE_CLASS)}
                tooltip={label}
            >
                {to ? <Link to={to}>{content}</Link> : content}
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}

function MobileShellHeader(): ReactElement {
    const { toggleSidebar } = useSidebar();

    return (
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4 md:hidden">
            <Button
                id="tour-mobile-menu"
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Abrir menu"
                onClick={toggleSidebar}
                className="-ml-2 size-10"
            >
                <HamburgerMenu className="size-5" />
            </Button>
            <SidebarBrandLogo />
        </header>
    );
}

function MobileSidebarCloseArea({ children }: PropsWithChildren): ReactElement {
    const { isMobile, setOpenMobile } = useSidebar();

    const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        if (!isMobile) return;

        const target = event.target as HTMLElement | null;
        if (target?.closest('a[href]')) {
            setOpenMobile(false);
        }
    };

    return (
        <div className="flex size-full min-h-0 flex-col" onClickCapture={handleClickCapture}>
            {children}
        </div>
    );
}

export default function SidebarProvider({ onLogout, children }: Props): ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { openModal, openWizardModal, closeModal } = useModalContext();
    const [open, setOpen] = useLocalStorage('dashboard_sidebar_open', true);
    const { hasSubscription, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
    const { flags } = useFeatureFlags();
    const { data: supportAgentStatus } = useQuery({
        queryKey: ['support', 'agent', 'me'],
        queryFn: supportApi.getAgentStatus,
        enabled: Boolean(user),
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const { data: featureFlagAdminStatus } = useQuery({
        queryKey: ['feature-flags', 'admin', 'me'],
        queryFn: featureFlagAdminApi.getAdminStatus,
        enabled: Boolean(user),
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const isSupportAgent = supportAgentStatus?.is_agent === true;
    const canManageFeatureFlags = featureFlagAdminStatus?.is_admin === true;
    const supportInboxPageUrl = createPageUrl(SupportInboxPage);
    const helpPageUrl = createPageUrl(HelpPage);
    const featureFlagsAdminPageUrl = createPageUrl(FeatureFlagsAdminPage);
    const visibleNavigationItems = navigationItems.filter(
        (item) => !item.featureFlag || flags[item.featureFlag]?.enabled === true,
    );

    const handleNewAnamnesis = () => {
        // No title here: the wizard renders its own dialog header so the title,
        // patient subtitle and back button stay in sync with the current step.
        openWizardModal({
            size: 'lg',
            content: (
                <NewAnamnesisWizard
                    onOpenEditor={(patientId, templateId, replaceExisting) => {
                        const { pathname, state } = anamnesisEditorRoute(
                            patientId,
                            templateId,
                            replaceExisting,
                        );
                        closeModal();
                        navigate(pathname, { state });
                    }}
                />
            ),
        });
    };

    const handleFeedback = () => {
        openModal({
            title: 'Enviar Feedback',
            content: <FeedbackModal />,
        });
    };

    return (
        <Provider open={open} onOpenChange={setOpen}>
            <div className="flex h-svh min-h-0 w-full overflow-hidden bg-background">
                <Sidebar collapsible="icon" className="z-40 border-r border-sidebar-border">
                    <MobileSidebarCloseArea>
                        <SidebarHeaderContent />

                        <SidebarContent className="p-3 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-3">
                            <SidebarGroup className="flex h-full flex-1 flex-col group-data-[collapsible=icon]:w-full">
                                <SidebarGroupContent className="flex h-full flex-1 flex-col group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center">
                                    <SidebarMenu className="flex h-full flex-1 flex-col group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:space-y-2">
                                        <div className="mb-3 group-data-[collapsible=icon]:mb-2 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        id="tour-sidebar-new-report"
                                                        className="w-full group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:p-0"
                                                    >
                                                        <Add />
                                                        <span className="group-data-[collapsible=icon]:sr-only">
                                                            Criar
                                                        </span>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start" className="w-56">
                                                    <DropdownMenuItem asChild>
                                                        <Link to={createPageUrl(ReportPage)}>
                                                            <DocumentText className="mr-2 size-4" />
                                                            Novo Laudo
                                                        </Link>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={handleNewAnamnesis}>
                                                        <Notepad2 className="mr-2 size-4" />
                                                        Nova anamnese
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        {visibleNavigationItems.map((item) => {
                                            const isActive =
                                                location.pathname === item.url ||
                                                (item.url === createPageUrl(PatientsPage) &&
                                                    location.pathname.startsWith('/patients'));

                                            return (
                                                <AppSidebarItem
                                                    key={item.title}
                                                    id={item.tourId}
                                                    label={item.title}
                                                    icon={item.icon}
                                                    to={item.url}
                                                    active={isActive}
                                                />
                                            );
                                        })}

                                        <div className="w-full p-2">
                                            <Separator />
                                        </div>

                                        <AppSidebarItem
                                            id="tour-sidebar-feedback"
                                            label="Enviar Feedback"
                                            icon={Message}
                                            onClick={handleFeedback}
                                        />

                                        {isSupportAgent ? (
                                            <AppSidebarItem
                                                label="Inbox de Suporte"
                                                icon={DirectInbox}
                                                to={supportInboxPageUrl}
                                                active={location.pathname === supportInboxPageUrl}
                                            />
                                        ) : null}

                                        {canManageFeatureFlags ? (
                                            <AppSidebarItem
                                                label="Feature Flags"
                                                icon={Flag}
                                                to={featureFlagsAdminPageUrl}
                                                active={
                                                    location.pathname === featureFlagsAdminPageUrl
                                                }
                                            />
                                        ) : null}

                                        <AppSidebarItem
                                            id="tour-sidebar-help"
                                            label="Ajuda"
                                            icon={Lifebuoy}
                                            to={helpPageUrl}
                                            active={
                                                location.pathname === helpPageUrl ||
                                                location.pathname.startsWith(`${helpPageUrl}/`)
                                            }
                                        />

                                        <AppSidebarItem
                                            id="tour-sidebar-subscription"
                                            label="Assinatura"
                                            icon={Card}
                                            to={createPageUrl(BillingPage)}
                                            active={
                                                location.pathname === createPageUrl(BillingPage)
                                            }
                                        />

                                        {!isSubscriptionLoading && !hasSubscription && (
                                            <SidebarMenuItem className="mt-auto pt-4 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center">
                                                <SidebarUpgradeButton />
                                            </SidebarMenuItem>
                                        )}
                                    </SidebarMenu>
                                </SidebarGroupContent>
                            </SidebarGroup>
                        </SidebarContent>

                        <SidebarFooter className="min-h-[88px] justify-end border-t border-sidebar-border p-4 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-end group-data-[collapsible=icon]:p-2">
                            <div className="flex items-center gap-3 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1">
                                <Link
                                    to={createPageUrl(ProfilePage)}
                                    className="flex min-w-0 flex-1 items-center gap-3 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-2"
                                >
                                    <UserAvatar
                                        name={user?.full_name}
                                        avatarUrl={user?.avatar_url}
                                        className="flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1 text-left group-data-[collapsible=icon]:hidden">
                                        <p className="truncate text-sm font-medium text-sidebar-foreground">
                                            {user?.full_name || LOADING_MESSAGE}
                                        </p>
                                        <p className="truncate text-xs text-muted-foreground">
                                            {user?.email || ''}
                                        </p>
                                    </div>
                                </Link>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="size-8 flex-shrink-0 hover:bg-sidebar-accent"
                                        >
                                            <Setting2 className="size-4 text-sidebar-foreground" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        <ThemeMenuItems />
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={onLogout}
                                            className="cursor-pointer text-red-600 focus:text-red-600"
                                        >
                                            <Logout className="mr-2 size-4" />
                                            Sair
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </SidebarFooter>
                    </MobileSidebarCloseArea>
                </Sidebar>

                <SidebarInset className="flex h-svh min-h-0 flex-1 flex-col overflow-hidden">
                    <MobileShellHeader />
                    {/* Pages that own their scrolling opt in via `PageShell fill`,
                        which is internally `overflow-hidden` and so can never grow
                        this container — no per-route casing needed here. */}
                    <div className="flex h-full min-h-0 flex-1 flex-col overflow-auto">
                        {children}
                    </div>
                </SidebarInset>
            </div>
        </Provider>
    );
}
